// Supabase Client Configuration
// Singleton client instance for the entire application

import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { environment } from '../../../environments/environment';
import type { Database } from './database.types';

let supabaseInstance: SupabaseClient<Database> | null = null;

/**
 * Custom storage adapter that gracefully handles Navigator LockManager errors
 * This wraps localStorage and catches lock acquisition errors that occur
 * when multiple tabs try to access the same Supabase auth token simultaneously.
 */
function createSafeStorageAdapter(): Storage | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }

  const originalStorage = window.localStorage;

  return {
    getItem: (key: string): string | null => {
      try {
        return originalStorage.getItem(key);
      } catch (error: any) {
        // Silently handle lock errors - they're non-critical
        if (error?.name === 'NavigatorLockAcquireTimeoutError' ||
            String(error).includes('NavigatorLockAcquireTimeoutError') ||
            String(error).includes('lock:sb-')) {
          console.debug('Storage lock error (non-critical):', error);
          // Return null to indicate no value, which is safe for Supabase
          return null;
        }
        throw error;
      }
    },
    setItem: (key: string, value: string): void => {
      try {
        originalStorage.setItem(key, value);
      } catch (error: any) {
        // Silently handle lock errors - they're non-critical
        if (error?.name === 'NavigatorLockAcquireTimeoutError' ||
            String(error).includes('NavigatorLockAcquireTimeoutError') ||
            String(error).includes('lock:sb-')) {
          console.debug('Storage lock error (non-critical):', error);
          // Silently fail - another tab will handle the update
          return;
        }
        throw error;
      }
    },
    removeItem: (key: string): void => {
      try {
        originalStorage.removeItem(key);
      } catch (error: any) {
        // Silently handle lock errors - they're non-critical
        if (error?.name === 'NavigatorLockAcquireTimeoutError' ||
            String(error).includes('NavigatorLockAcquireTimeoutError') ||
            String(error).includes('lock:sb-')) {
          console.debug('Storage lock error (non-critical):', error);
          // Silently fail - another tab will handle the update
          return;
        }
        throw error;
      }
    },
    get length(): number {
      try {
        return originalStorage.length;
      } catch (error: any) {
        if (error?.name === 'NavigatorLockAcquireTimeoutError' ||
            String(error).includes('NavigatorLockAcquireTimeoutError') ||
            String(error).includes('lock:sb-')) {
          return 0;
        }
        throw error;
      }
    },
    clear(): void {
      try {
        originalStorage.clear();
      } catch (error: any) {
        if (error?.name === 'NavigatorLockAcquireTimeoutError' ||
            String(error).includes('NavigatorLockAcquireTimeoutError') ||
            String(error).includes('lock:sb-')) {
          return;
        }
        throw error;
      }
    },
    key: (index: number): string | null => {
      try {
        return originalStorage.key(index);
      } catch (error: any) {
        if (error?.name === 'NavigatorLockAcquireTimeoutError' ||
            String(error).includes('NavigatorLockAcquireTimeoutError') ||
            String(error).includes('lock:sb-')) {
          return null;
        }
        throw error;
      }
    },
  } as Storage;
}

/**
 * Get or create Supabase client instance
 * @returns Singleton Supabase client
 */
export function getSupabaseClient(): SupabaseClient<Database> {
  if (!supabaseInstance) {
    const supabaseUrl = environment.supabase.url;
    const supabaseKey = environment.supabase.anonKey;

    if (!supabaseUrl || !supabaseKey) {
      throw new Error(
        'Supabase URL and Anon Key must be set in environment configuration'
      );
    }

    if (supabaseUrl === 'YOUR_SUPABASE_URL' || supabaseKey === 'YOUR_SUPABASE_ANON_KEY') {
      throw new Error(
        'Please configure your Supabase credentials in src/environments/environment.ts'
      );
    }

    // Use custom storage adapter that handles lock errors gracefully
    const safeStorage = createSafeStorageAdapter();

    supabaseInstance = createClient<Database>(supabaseUrl, supabaseKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
        storage: safeStorage,
      },
    });
  }

  return supabaseInstance;
}

/**
 * Reset the Supabase client instance (useful for testing)
 */
export function resetSupabaseClient(): void {
  supabaseInstance = null;
}
